import {useTaggingClick} from "@/hooks/common/useTagging";
import {getTarget, onClickSSO} from "@COMPONENTS/common/commonUtil";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import SSOLink from "@COMPONENTS/common/ssoLink";
import * as style from "@FRAMEWORK/ui/desktop/header/index";
import useGlobalMenu, {GlobalMenu, GlobalMenuBff} from "@QUERIES/bff/global/useGlobalMenu";
import {useEffect, useRef, useState} from "react";

type Props = {
	handleDim: (is: boolean) => void;
};

type PropsLv2 = {
	globalMenu: GlobalMenuBff;
	hoverLv1: string;
	handelEnterLv2: () => void;
	handelLeaveLv2: () => void;
	taggingClick: (menu: GlobalMenu) => void;
};

type PropsLv3 = {
	lv1: GlobalMenu;
	lv2: GlobalMenu;
	lv3List: GlobalMenu[];
	proMenuList: GlobalMenu[];
	taggingClick: (menu: GlobalMenu) => void;
};

const Lv3List = (props: PropsLv3) => {
	const {lv1, lv2, lv3List, proMenuList, taggingClick} = props;
	const localLv3 = lv3List.filter(o => o.locTypeCd !== "T" && o.homeYn === "Y") || [];
	const themaLv3 = lv3List.filter(o => o.locTypeCd === "T" && o.homeYn === "Y") || [];
	const promotionLv3 = proMenuList.filter(o => o.dspSid === lv2.dspSid) || [];
	const isLv3 = lv1.areaMainYn === "Y";
	const isLocalType = lv2.gnbTypeCd === "L";

	return (
		<>
			{isLv3 && (
				<>
					<style.Category>
						<style.CategoryWrap>
							<style.CategoryArea>
								<style.CategorySubtitle>
									지역
									<SSOLink href={lv2.dspUrl || "#"} onClick={() => taggingClick(lv2)}>
										[{lv1.dspNm}] {lv2.dspNm}
									</SSOLink>
								</style.CategorySubtitle>
								<style.List>
									{localLv3.map((local, localIndex) => (
										<li key={local.dspSid + localIndex}>
											<SSOLink
												href={local.dspUrl || "#"}
												target={getTarget(local.webMenuTarget)}
												onClick={e => {
													taggingClick({...local, dspOrder: localIndex + 1});
												}}
											>
												{local.dspNm}
											</SSOLink>
										</li>
									))}
								</style.List>
							</style.CategoryArea>
							<style.CategoryArea>
								{!isLocalType && themaLv3.length > 0 && (
									<>
										<style.CategorySubtitle>테마별</style.CategorySubtitle>
										<style.List>
											{themaLv3.map((thema, themaIndex) => (
												<li key={thema.dspSid + themaIndex}>
													<SSOLink
														href={thema.dspUrl || "#"}
														target={getTarget(thema.webMenuTarget)}
														onClick={e => {
															taggingClick({...thema, dspOrder: themaIndex + 1});
														}}
													>
														{thema.dspNm}
													</SSOLink>
												</li>
											))}
										</style.List>
									</>
								)}
							</style.CategoryArea>
							<style.CategoryArea>
								{promotionLv3.length > 0 && (
									<>
										<style.CategorySubtitle>프로모션</style.CategorySubtitle>
										<style.List>
											{promotionLv3.map((promotion, promotionIndex) => (
												<li key={promotion.dspSid + promotionIndex}>
													<SSOLink
														href={promotion.dspUrl || "#"}
														target={getTarget(promotion.webMenuTarget)}
														onClick={e => {
															taggingClick({...promotion, dspOrder: promotionIndex + 1});
														}}
													>
														{promotion.subDspNm}
													</SSOLink>
												</li>
											))}
										</style.List>
									</>
								)}
							</style.CategoryArea>
						</style.CategoryWrap>
					</style.Category>
				</>
			)}
		</>
	);
};

const Lv2List = (props: PropsLv2) => {
	const {globalMenu, hoverLv1, handelEnterLv2, handelLeaveLv2, taggingClick} = props;
	const {menuLv1List, menuLv2List, menuLv3List, proMenuList} = globalMenu;
	return (
		<>
			{menuLv2List
				.filter(o => o.dspSidHi === hoverLv1 && o.homeYn === "Y")
				.map((lv2, lv2Index) => {
					const lv1 = menuLv1List.find(o => o.dspSid === lv2.dspSidHi); // 1depth
					const lv3List = menuLv3List.filter(o => o.dspSidHi === lv2.dspSid && o.homeYn === "Y") || [];
					const isLv3 = lv1.areaMainYn === "Y";

					return (
						<style.LnbItem
							key={lv2.dspSid + lv2Index}
							onMouseEnter={() => isLv3 && handelEnterLv2()}
							onMouseLeave={() => isLv3 && handelLeaveLv2()}
						>
							<SSOLink
								href={lv2.dspUrl || "#"}
								target={getTarget(lv2.webMenuTarget)}
								onClick={e => {
									taggingClick({...lv2, dspOrder: lv2Index + 1});
								}}
							>
								{lv2.dspNm}
							</SSOLink>
							{/* 3 Depth */}
							<Lv3List lv1={lv1} lv2={lv2} lv3List={lv3List} proMenuList={proMenuList} taggingClick={taggingClick} />
						</style.LnbItem>
					);
				})}
		</>
	);
};

const GNBComponent = (props: Props) => {
	const {handleDim} = props;

	/** 메뉴 관련 API */
	const globalMenuFetch = useGlobalMenu();
	const globalMenu = globalMenuFetch.data?.body;
	const menuLv1List = globalMenu?.menuLv1List;
	const subMenuList = globalMenu?.subMenuList;

	const centerRef = useRef<HTMLLIElement[]>([]);
	const depth2WrapRef = useRef<HTMLUListElement[]>([]);
	const naviWrapRef = useRef<HTMLUListElement>(null);

	const [hoverLv1, setHoverLv1] = useState<string>(null); // dspSid
	const [hoverIndex, setHoverIndex] = useState(0);
	const handleEnterLv1 = (index: number, dspSid: string) => {
		setHoverLv1(dspSid);
		setHoverIndex(index);
	};

	// LV2 에 LV3 가 있을 경우 dim 보여주기
	const handelEnterLv2 = () => handleDim(true);
	const handelLeaveLv2 = () => handleDim(false);

	useEffect(() => {
		if (!menuLv1List) return;
		const isAlign = menuLv1List.some(v => v.dspSid === hoverLv1 && v.align === "N");
		// 2 Depth가 center로 설정 될 경우 (align === "N")
		if (!isAlign || !hoverIndex || !centerRef.current || !depth2WrapRef.current || !naviWrapRef.current) return;
		const centerElement = centerRef.current[hoverIndex];
		const depth2Wrap = depth2WrapRef.current[hoverIndex];
		const naviWrap = naviWrapRef.current;

		const position = centerElement.getBoundingClientRect();
		const naviPosition = naviWrap.getBoundingClientRect();

		// // 자식 요소들의 전체 width 값을 구하기
		const totalWidth = Array.from(depth2Wrap.children).reduce((total, child) => {
			const childElement = child as HTMLElement;
			return total + childElement.offsetWidth;
		}, 0);

		depth2Wrap.style.paddingLeft = `${position.left - naviPosition.left - totalWidth / 2 + position.width / 2}px`;
	}, [hoverLv1]);

	const {taggingClick} = useTaggingClick("gnb_click");

	return (
		<style.Navi>
			<ul ref={naviWrapRef}>
				{/* 1 Depth */}
				{menuLv1List &&
					menuLv1List
						.filter(o => o.homeYn === "Y")
						.map((item, index) => (
							<li key={index} ref={el => (centerRef.current[index] = el)}>
								<style.GnbItem
									href={item.dspUrl || "#"}
									target={getTarget(item.webMenuTarget)}
									onClick={e => {
										taggingClick({...item, dspOrder: index + 1});
										onClickSSO(e, item.dspUrl || "#");
									}}
									onMouseEnter={() => handleEnterLv1(index, item.dspSid)}
								>
									{item.iconYn === "Y" && item.iconType && <style.IconBadge>{item.iconType}</style.IconBadge>}
									{item.dspNm}
								</style.GnbItem>
								<style.Lnb>
									<style.LnbWrap>
										<ul ref={el => (depth2WrapRef.current[index] = el)}>
											{/* 2 Depth */}
											<Lv2List
												globalMenu={globalMenu}
												hoverLv1={hoverLv1}
												handelEnterLv2={handelEnterLv2}
												handelLeaveLv2={handelLeaveLv2}
												taggingClick={taggingClick}
											/>
										</ul>
										<style.EventNavi>
											{/* sub */}
											{subMenuList &&
												subMenuList
													.filter(o => o.dspSid === hoverLv1 && o.subDspTypeCd === "SUB")
													.map((sub, subIndex) => (
														<li key={subIndex}>
															<SSOLink href={sub.dspUrl || "#"} target={getTarget(sub.webMenuTarget)}>
																{sub.subDspNm}
															</SSOLink>
														</li>
													))}
										</style.EventNavi>
									</style.LnbWrap>
								</style.Lnb>
							</li>
						))}
			</ul>
		</style.Navi>
	);
};

const GNB = (props: Props) => (
	<ErrorBoundary logMsg="GNB Error">
		<GNBComponent {...props} />
	</ErrorBoundary>
);

export default GNB;
